import { useEffect, useRef, useState } from "react";
import { Button, Callout, Dialog, Skeleton } from "@obsina/design-system";
import { ControlPlaneError } from "../api";
import { fetchSourceText } from "./api";
import {
  formatByteSize,
  formatItemDate,
  shownTextState,
  untitledLabel,
  withdrawalKind,
} from "./data";
import type { SourceBlock, SourceItem, SourceTextResult } from "./data";
import { sourcesMessages } from "./messages";
import type { SourcesMessages } from "./messages";

/**
 * The canonical text of one collected item.
 *
 * Two rules shape this component. The text is rendered as **text**: every block
 * is placed as a React child, never as HTML, so an item's own content — which is
 * untrusted data arriving from a mailbox — cannot inject markup or instructions
 * into the page. Block structure is preserved by rendering one element per
 * `DocumentBlock` with `white-space: pre-wrap`, which keeps the line and
 * paragraph shape the parser recorded without interpreting it.
 *
 * And an absence is always explained. `fetch` deliberately answers `unknown` for
 * a document whose coverage is not established, and an item may have no
 * published document at all; both are stated in the reader's terms, next to what
 * would change them.
 */
export function SourceTextDialog({
  item,
  apiBase,
  asOf,
  open,
  onOpenChange,
  messages = sourcesMessages,
  channelLabel,
}: {
  item: SourceItem | null;
  apiBase: string | null;
  asOf: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messages?: SourcesMessages;
  channelLabel: (item: SourceItem) => string;
}) {
  const [text, setText] = useState<SourceTextResult | null>(null);
  const [blocks, setBlocks] = useState<readonly SourceBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const mounted = useRef(true);
  // Bumped on every reload so a page resolving late cannot append one item's
  // text onto another's.
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const versionId = item?.documentVersionId ?? null;

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    setText(null);
    setBlocks([]);
    setFailed(false);
    setLoadingMore(false);
    if (!open || !apiBase || !asOf || !versionId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    fetchSourceText(
      apiBase,
      { asOf, documentVersionId: versionId },
      controller.signal,
    )
      .then((result) => {
        if (controller.signal.aborted || generation.current !== current) return;
        setText(result);
        setBlocks(result.blocks);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || generation.current !== current) return;
        // An aborted read is the caller closing the dialog, not a failure.
        if (error instanceof ControlPlaneError || error instanceof Error) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && generation.current === current) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [apiBase, asOf, open, versionId, retry]);

  const loadMore = () => {
    if (!apiBase || !asOf || !versionId || !text?.nextCursor || loadingMore) {
      return;
    }
    const current = generation.current;
    setLoadingMore(true);
    fetchSourceText(apiBase, {
      asOf,
      documentVersionId: versionId,
      cursor: text.nextCursor,
    })
      .then((result) => {
        if (!mounted.current || generation.current !== current) return;
        setBlocks((previous) => [...previous, ...result.blocks]);
        setText((previous) =>
          previous ? { ...previous, nextCursor: result.nextCursor } : previous,
        );
      })
      .catch(() => {
        if (mounted.current && generation.current === current) setFailed(true);
      })
      .finally(() => {
        if (mounted.current && generation.current === current) {
          setLoadingMore(false);
        }
      });
  };

  // The same label the row carries, so opening a row does not rename it. The
  // channel is stated on its own line below, which is why it is not the
  // fallback here.
  const title = item
    ? (item.title ?? untitledLabel(item, messages.table))
    : messages.dialog.close;

  return (
    <Dialog
      className="source-dialog"
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={title}
    >
      {item && (
        <div className="source-dialog__stack">
          <p className="source-dialog__meta">
            <span>{channelLabel(item)}</span>
            <span>{formatItemDate(item.occurredAt)}</span>
            <span>
              {item.visibility === "owner_private"
                ? messages.dialog.meta.visibilityPrivate
                : messages.dialog.meta.visibilityShared}
            </span>
            {item.mediaType && <span>{item.mediaType}</span>}
            {/*
              The one measurement a refused item still has. The list column
              carries it and the dialog did not, so opening a row that could not
              be read replaced every number on screen with a paragraph — and the
              size is exactly what a reader weighs an unread attachment by.
            */}
            {formatByteSize(item.byteSize) && (
              <span>{formatByteSize(item.byteSize)}</span>
            )}
            {text?.status === "ok" && (
              <span>
                {messages.dialog.meta.blocks(
                  blocks.length,
                  text.totalBlockCount,
                )}
              </span>
            )}
            {item.characterCount !== null && (
              <span>
                {messages.dialog.meta.characters(item.characterCount)}
              </span>
            )}
          </p>
          {/*
            The layer label is a claim about provenance, so it is stated as a
            sentence rather than a chip: an OCR reading shown without it reads as
            the provider's own words.
          */}
          {(text?.layerKind ?? item.layerKind) && (
            <p className="source-dialog__layer">
              {(text?.layerKind ?? item.layerKind) === "ocr_derived"
                ? messages.dialog.meta.derived
                : messages.dialog.meta.canonical}
            </p>
          )}
          <SourceTextBody
            blocks={blocks}
            failed={failed}
            item={item}
            loading={loading}
            loadingMore={loadingMore}
            messages={messages}
            onLoadMore={loadMore}
            onRetry={() => setRetry((value) => value + 1)}
            text={text}
          />
          {item.sourceUrl && (
            <p className="source-dialog__source">
              <a
                href={item.sourceUrl}
                rel="noreferrer noopener"
                target="_blank"
              >
                {messages.dialog.openSource}
              </a>
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Which refusal this is, in the reader's terms.
 *
 * `document_incomplete` is about the item they opened and is settled by
 * definition. A coverage refusal is about the tenant, and whether it is worth
 * waiting for is exactly what `durability` answers — so the copy is chosen from
 * it rather than assuming one way or the other. An envelope that states no
 * durability gets the wording that promises neither.
 */
function withheldCopy(
  text: SourceTextResult,
  messages: SourcesMessages,
): { readonly title: string; readonly body: string } {
  const withheld = messages.dialog.withheld;
  if (text.reason === "document_incomplete") return withheld.incomplete;
  if (text.durability === "terminal") return withheld.terminal;
  if (text.durability === "transient" || text.durability === "mixed") {
    return withheld.transient;
  }
  return withheld.unstated;
}

/**
 * Why this partial reading stopped short.
 *
 * The API releases the pipeline's own classification beside a `partial` verdict
 * and nothing else. A null is not a missing sentence: it means no reading
 * stands behind the item, which is its own answer and is stated as one. That is
 * the whole of the three-valued rule here — a recorded cause, a recorded
 * reading that explains nothing, and no reading at all are three different
 * facts and none of them is silence.
 */
function partialCauseSentence(
  item: SourceItem,
  messages: SourcesMessages,
): string {
  return item.partialReason === null
    ? messages.dialog.partialCauseAbsent
    : messages.dialog.partialCause[item.partialReason];
}

function SourceTextBody({
  item,
  text,
  blocks,
  loading,
  loadingMore,
  failed,
  messages,
  onLoadMore,
  onRetry,
}: {
  item: SourceItem;
  text: SourceTextResult | null;
  blocks: readonly SourceBlock[];
  loading: boolean;
  loadingMore: boolean;
  failed: boolean;
  messages: SourcesMessages;
  onLoadMore: () => void;
  onRetry: () => void;
}) {
  if (item.documentVersionId === null) {
    // Read through the same function the list badge reads. The two used to
    // derive their wording independently, which is how one row could be "Read
    // in full" in the list and "Text not published" here.
    const state = shownTextState(item);
    const absent =
      state === "withdrawn"
        ? messages.dialog.absent.withdrawn[withdrawalKind(item)]
        : messages.dialog.absent[state];
    // The recorded reason when there is one, the status wording when there is
    // not. Never both: they answer the same question, and the reason answers it
    // with what actually happened rather than with what the status implies.
    const reason =
      item.unreadableReason === null
        ? null
        : messages.dialog.reason[item.unreadableReason];
    return (
      <Callout title={absent.title} tone="info">
        {reason ?? absent.body}
        {state === "partial" && (
          <p className="source-dialog__cause-gap">
            {partialCauseSentence(item, messages)}
          </p>
        )}
      </Callout>
    );
  }
  if (loading) {
    return (
      <div aria-label={messages.dialog.loading} aria-live="polite">
        <Skeleton height="1rem" />
        <Skeleton height="1rem" />
        <Skeleton height="1rem" />
      </div>
    );
  }
  if (failed && blocks.length === 0) {
    return (
      <Callout title={messages.dialog.loadFailed} tone="danger">
        <Button onClick={onRetry} size="sm" variant="secondary">
          {messages.dialog.retry}
        </Button>
      </Callout>
    );
  }
  if (text?.status === "unknown") {
    const withheld = withheldCopy(text, messages);
    return (
      <Callout title={withheld.title} tone="warning">
        {withheld.body}
        {text.reason === "document_incomplete" && (
          <p className="source-dialog__cause-gap">
            {partialCauseSentence(item, messages)}
          </p>
        )}
      </Callout>
    );
  }
  return (
    <>
      <div className="source-text">
        {blocks.map((block) => (
          <p className="source-text__block" key={block.documentBlockId}>
            {block.markdown}
          </p>
        ))}
      </div>
      {text?.nextCursor && (
        <Button
          disabled={loadingMore}
          onClick={onLoadMore}
          size="sm"
          variant="secondary"
        >
          {messages.dialog.moreBlocks}
        </Button>
      )}
      {failed && blocks.length > 0 && (
        <p className="source-dialog__inline-error" role="status">
          {messages.dialog.loadFailed}
        </p>
      )}
    </>
  );
}
